import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DeleteRecurringInvoiceRequest,
  GetRecurringInvoiceRequest,
  ListRecurringInvoicesFilters as ListRecurringInvoicesFiltersApi,
  ListRecurringInvoicesRequest,
  ListRecurringInvoicesResponse,
  RecurringInvoice as RecurringInvoiceApi,
  RecurringInvoiceApiService,
  UpdateRecurringInvoiceRequest,
} from '@vendasta/billing';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagedResponse } from '../core';
import { convertToApiCollectionMethod } from './collection-method';
import { convertToApiInterval } from './interval';
import { convertToApiPaymentProcessor } from './payment-processor';
import {
  CreateRecurringInvoiceRequest,
  ListRecurringInvoicesByCustomerID,
  ListRecurringInvoicesByMerchantID,
  ListRecurringInvoicesFilters,
  RecurringInvoice,
  convertFromApiRecurringInvoice,
  convertToApiRecurringInvoiceItem,
} from './recurring-invoice';
import { convertToApiRecurringInvoiceStatus } from './status';
import { paymentMethodTypesToApi } from '../retail-customer-configuration';

@Injectable({ providedIn: 'root' })
export class RecurringInvoiceService {
  constructor(private recurringInvoiceApiService: RecurringInvoiceApiService) {}

  create(request: CreateRecurringInvoiceRequest): Observable<RecurringInvoice> {
    const req = new RecurringInvoiceApi({
      ...request,
      status: convertToApiRecurringInvoiceStatus(request.status),
      interval: convertToApiInterval(request.interval),
      collectionMethod: convertToApiCollectionMethod(request.collectionMethod),
      paymentMethodTypes: paymentMethodTypesToApi(request.paymentMethodTypes),
      paymentProcessor: convertToApiPaymentProcessor(request.paymentProcessor),
      items: (request.items || []).map((i) => convertToApiRecurringInvoiceItem(i)),
    });

    return this.recurringInvoiceApiService
      .createRecurringInvoice(req)
      .pipe(map((res: RecurringInvoiceApi) => convertFromApiRecurringInvoice(res)));
  }

  get(id: string): Observable<RecurringInvoice> {
    const req = new GetRecurringInvoiceRequest({
      id,
    });
    return this.recurringInvoiceApiService
      .getRecurringInvoice(req)
      .pipe(map((resp: RecurringInvoiceApi) => convertFromApiRecurringInvoice(resp)));
  }

  delete(id: string): Observable<HttpResponse<null>> {
    const req = new DeleteRecurringInvoiceRequest({
      id,
    });
    return this.recurringInvoiceApiService.deleteRecurringInvoice(req);
  }

  list(
    identifier: ListRecurringInvoicesByCustomerID | ListRecurringInvoicesByMerchantID,
    cursor: string,
    pageSize: number,
    filters?: ListRecurringInvoicesFilters,
  ): Observable<PagedResponse<RecurringInvoice>> {
    const req = new ListRecurringInvoicesRequest({
      pagingOptions: {
        cursor,
        pageSize,
      },
    });
    if (identifier instanceof ListRecurringInvoicesByCustomerID) {
      req.customerId = identifier.customerId;
    } else if (identifier instanceof ListRecurringInvoicesByMerchantID) {
      req.merchantId = identifier.merchantId;
    }
    if (filters) {
      req.filters = new ListRecurringInvoicesFiltersApi({
        statuses: filters.statuses?.map((status) => convertToApiRecurringInvoiceStatus(status)),
        intervals: filters.detailedInterval?.map((i) => {
          return { interval: convertToApiInterval(i.interval), intervalCount: i.intervalCount };
        }),
        nextIssuedDate: filters.dateFilter,
      });
    }
    return this.recurringInvoiceApiService.listRecurringInvoices(req).pipe(
      map((resp: ListRecurringInvoicesResponse) => {
        const invoices = (resp.recurringInvoices || []).map((item) => convertFromApiRecurringInvoice(item));
        return new PagedResponse(invoices, resp.pagingMetadata.nextCursor, resp.pagingMetadata.hasMore);
      }),
    );
  }

  update(recurringInvoice: RecurringInvoice, updateMask?: string[]): Observable<RecurringInvoice> {
    const req = new UpdateRecurringInvoiceRequest({
      recurringInvoice: new RecurringInvoiceApi({
        ...recurringInvoice,
        status: convertToApiRecurringInvoiceStatus(recurringInvoice.status),
        interval: convertToApiInterval(recurringInvoice.interval),
        collectionMethod: convertToApiCollectionMethod(recurringInvoice.collectionMethod),
        paymentMethodTypes: paymentMethodTypesToApi(recurringInvoice.paymentMethodTypes),
        paymentProcessor: convertToApiPaymentProcessor(recurringInvoice.paymentProcessor),
        items: (recurringInvoice.items || []).map((i) => convertToApiRecurringInvoiceItem(i)),
      }),
      updateMask: updateMask ? { paths: updateMask } : undefined,
    });

    return this.recurringInvoiceApiService
      .updateRecurringInvoice(req)
      .pipe(map((res: RecurringInvoiceApi) => convertFromApiRecurringInvoice(res)));
  }
}
